"use client";

import React, { useState, useMemo } from "react";
import { CircleCheck, FileCheck, FileText, Layers, SlidersHorizontal, Ruler, ShieldCheck, CircleHelp, Copy, Check, ArrowRight, Download } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import DataTable, { type DataTableColumn } from "@/components/common/DataTable";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface SpecItem {
  id: string;
  step: string;
  title: Record<Lang, string>;
  field: Record<Lang, string>;
  description: Record<Lang, string>;
  examples: Record<Lang, string>;
  criticalNote: Record<Lang, string>;
  iconName: "layers" | "sliders" | "ruler" | "shield" | "file";
}
const specItems: SpecItem[] = [{
  id: "alloy",
  step: "01",
  title: {
    en: "Material alloy and grade",
    zh: "材质牌号与合金成分",
    es: "Aleación y calidad del material",
    de: "Werkstofflegierung und Güte",
    fr: "Nuance et alliage du matériau",
    pt: "Liga e especificação do material"
  },
  field: {
    en: "Alloy Grade",
    zh: "合金牌号 (Alloy Grade)",
    es: "Calidad de aleación (Alloy Grade)",
    de: "Werkstoffgüte (Alloy Grade)",
    fr: "Nuance d'alliage (Alloy Grade)",
    pt: "Grau da liga (Alloy Grade)"
  },
  description: {
    en: "Specify exact stainless steel chemistry. Generic 'stainless' descriptions risk improper corrosion or thermal resistance.",
    zh: "请明确指定具体的不锈钢牌号。通用的“不锈钢”描述可能导致耐腐蚀或耐温等级不达标。",
    es: "Especifique la química exacta del acero inoxidable. Las descripciones genéricas conllevan riesgos de corrosión inadecuada.",
    de: "Geben Sie die genaue Edelstahlbezeichnung an. Allgemeine Bezeichnungen bergen Risiken bei Korrosions- oder Temperaturbelastung.",
    fr: "Indiquez la composition exacte de l'acier inoxydable. Une mention générique risque d'entraîner une résistance thermique ou chimique inadaptée.",
    pt: "Especifique a composição exata do aço inoxidável. Descrições genéricas podem comprometer a resistência à corrosão ou térmica."
  },
  examples: {
    en: "AISI 304, AISI 304L, AISI 316, AISI 316L, AISI 314",
    zh: "AISI 304, AISI 304L, AISI 316, AISI 316L, AISI 314",
    es: "AISI 304, AISI 304L, AISI 316, AISI 316L, AISI 314",
    de: "AISI 304, AISI 304L, AISI 316, AISI 316L, AISI 314",
    fr: "AISI 304, AISI 304L, AISI 316, AISI 316L, AISI 314",
    pt: "AISI 304, AISI 304L, AISI 316, AISI 316L, AISI 314"
  },
  criticalNote: {
    en: "Specify 316L over 316 if parts will undergo subsequent welding or cleanroom chemical passivation.",
    zh: "若后续需进行焊接加工或洁净室化学钝化处理，请优先指定 316L 而非 316。",
    es: "Especifique 316L en lugar de 316 si las piezas se someterán a soldadura posterior o pasivado químico en sala limpia.",
    de: "Wählen Sie 316L statt 316, wenn Bauteile nachträglich geschweißt oder chemisch passiviert werden.",
    fr: "Spécifiez le 316L plutôt que le 316 si les pièces doivent être soudées ou subir une passivation chimique en salle blanche.",
    pt: "Especifique 316L em vez de 316 se as peças forem submetidas a soldagem posterior ou passivação química em sala limpa."
  },
  iconName: "layers"
}, {
  id: "weave",
  step: "02",
  title: {
    en: "Weave construction",
    zh: "编织结构与编织类型",
    es: "Tipo de tejido",
    de: "Webart und Bindung",
    fr: "Type de tissage",
    pt: "Construção da trama"
  },
  field: {
    en: "Weave Pattern",
    zh: "编织类型 (Weave Pattern)",
    es: "Patrón de tejido (Weave Pattern)",
    de: "Webmuster (Weave Pattern)",
    fr: "Armure de tissage (Weave Pattern)",
    pt: "Padrão de trama (Weave Pattern)"
  },
  description: {
    en: "State the geometric weave pattern. Different weaves offer starkly different flow resistance, mechanical stability, and surface pore geometry.",
    zh: "明确编织几何结构。不同编织形式的流动阻力、机械稳定性和表面孔隙微观形貌差异显著。",
    es: "Indique el patrón geométrico del tejido. Cada tipo ofrece resistencias de flujo, estabilidad mecánica y geometría de poros sustancialmente distintas.",
    de: "Nennen Sie das geometrische Webmuster. Unterschiedliche Bindungen variieren deutlich in Durchflusswiderstand, Stabilität und Porengeometrie.",
    fr: "Précisez l'armure géométrique du tissage. Les différentes armures présentent des écarts notables de perte de charge, de tenue mécanique et de géométrie de pores.",
    pt: "Informe o padrão geométrico da trama. Cada tipo proporciona resistência de fluxo, estabilidade mecânica e geometria de poros bastante distintas."
  },
  examples: {
    en: "Plain Weave, Twilled Weave, Plain Dutch (PDW), Twilled Dutch (TDW), Reverse Dutch (RPD)",
    zh: "平纹编织 (Plain), 斜纹编织 (Twilled), 平纹密纹 (PDW), 斜纹密纹 (TDW), 反向密纹 (RPD)",
    es: "Tejido liso (Plain), sarga (Twilled), Dutch liso (PDW), Dutch cruzado (TDW), Dutch inverso (RPD)",
    de: "Leinwandbindung (Plain), Köperbindung (Twilled), Glatte Tresse (PDW), Köpertresse (TDW), Panzertresse (RPD)",
    fr: "Tissage uni (Plain), tissage sergé (Twilled), hollandais uni (PDW), hollandais sergé (TDW), hollandais inversé (RPD)",
    pt: "Trama lisa (Plain), trama sarja (Twilled), holandesa lisa (PDW), holandesa sarja (TDW), holandesa invertida (RPD)"
  },
  criticalNote: {
    en: "Dutch weaves carry distinct warp and weft counts; always specify warp x weft.",
    zh: "密纹网（席形网）经纬目数不同，请务必以“经向目数 x 纬向目数”格式标注。",
    es: "Los tejidos Dutch tienen recuentos distintos de urdimbre y trama; especifique siempre urdimbre x trama.",
    de: "Tressengewebe haben unterschiedliche Kett- und Schusszahlen; geben Sie stets Kette x Schuss an.",
    fr: "Les toiles hollandaises possèdent des nombres de fils distincts en chaîne et trame ; précisez toujours chaîne x trame.",
    pt: "As tramas holandesas têm contagens distintas de urdume e trama; sempre especifique urdume x trama."
  },
  iconName: "sliders"
}, {
  id: "mesh-aperture",
  step: "03",
  title: {
    en: "Mesh count and aperture rating",
    zh: "目数与孔径/过滤精度",
    es: "Número de mallas y apertura",
    de: "Maschenzahl und Maschenweite",
    fr: "Nombre de mailles et ouverture",
    pt: "Contagem de malha e abertura"
  },
  field: {
    en: "Mesh Count & Aperture",
    zh: "目数与孔径 (Mesh & Aperture)",
    es: "Malla y apertura (Mesh & Aperture)",
    de: "Maschenzahl & Weite (Mesh & Aperture)",
    fr: "Mailles & ouverture (Mesh & Aperture)",
    pt: "Malha & abertura (Mesh & Aperture)"
  },
  description: {
    en: "Provide either nominal aperture (microns / mm) or mesh count per linear inch alongside exact wire diameter for both warp and weft directions.",
    zh: "提供名义公称孔径（微米/毫米）或每英寸目数，并同时注明经纬向丝径尺寸。",
    es: "Facilite la apertura nominal (micras / mm) o el número de mallas por pulgada lineal junto con el diámetro exacto del alambre de urdimbre y trama.",
    de: "Geben Sie entweder die Nennmaschenweite (µm / mm) oder die Maschenzahl pro Zoll zusammen mit dem genauen Drahtdurchmesser für Kette und Schuss an.",
    fr: "Fournissez l'ouverture nominale (microns / mm) ou le nombre de mailles par pouce linéaire avec le diamètre exact des fils en chaîne et en trame.",
    pt: "Indique a abertura nominal (mícrons / mm) ou a contagem de malhas por polegada linear com o diâmetro exato do arame de urdume e trama."
  },
  examples: {
    en: "100 x 100 mesh w/ 0.10mm wire (150 µm opening) OR 24 x 110 Dutch (80 µm nominal)",
    zh: "100 x 100 目 / 丝径 0.10mm (150 µm 开孔) 或 24 x 110 密纹网 (名义精度 80 µm)",
    es: "Malla 100 x 100 con alambre de 0,10 mm (abertura 150 µm) O Dutch 24 x 110 (80 µm nominal)",
    de: "100 x 100 Mesh mit 0,10 mm Draht (150 µm Weite) ODER 24 x 110 Tresse (80 µm nominal)",
    fr: "Toile 100 x 100 avec fil 0,10 mm (ouverture 150 microns) OU hollandais 24 x 110 (80 microns nominal)",
    pt: "Malha 100 x 100 com arame de 0,10 mm (abertura 150 µm) OU holandesa 24 x 110 (80 µm nominal)"
  },
  criticalNote: {
    en: "Providing both mesh count and wire diameter prevents ambiguity in total open area calculations.",
    zh: "同时提供目数和丝径可消除开孔率与有效通流面积计算的歧义。",
    es: "Indicar tanto la malla como el diámetro del alambre elimina ambigüedades en el cálculo del área abierta.",
    de: "Die Angabe von Maschenzahl und Drahtdurchmesser vermeidet Unklarheiten bei der offenen Siebfläche.",
    fr: "Préciser à la fois le nombre de mailles et le diamètre du fil évite toute ambiguïté sur la surface ouverte.",
    pt: "Informar a contagem de malha e o diâmetro do arame elimina ambiguidades no cálculo da área aberta."
  },
  iconName: "ruler"
}, {
  id: "dimensions",
  step: "04",
  title: {
    en: "Dimensions and delivery format",
    zh: "供货规格与交货形态",
    es: "Dimensiones y formato de entrega",
    de: "Abmessungen und Lieferform",
    fr: "Dimensions et format de livraison",
    pt: "Dimensões e formato de fornecimento"
  },
  field: {
    en: "Roll / Cut Geometry",
    zh: "卷料/切割几何尺寸 (Roll / Cut Geometry)",
    es: "Geometría de rollo/corte (Roll / Cut Geometry)",
    de: "Rollen-/Zuschnittgeometrie (Roll / Cut Geometry)",
    fr: "Géométrie rouleau/découpe (Roll / Cut Geometry)",
    pt: "Geometria de rolo/corte (Roll / Cut Geometry)"
  },
  description: {
    en: "Define the physical supply format: continuous master roll, slit ribbon coil, stamped circular discs, or laser-cut custom blanks.",
    zh: "明确交货形态：原装连续大卷、分切窄带分条卷、冲压圆形滤片或激光切割定制坯料。",
    es: "Defina el formato de suministro: rollo continuo, bobina flejada, discos estampados circulares o piezas cortadas con láser.",
    de: "Legen Sie die Lieferform fest: Master-Rolle, Spaltband, gestanzte Ronden oder lasergeschnittene Formteile.",
    fr: "Définissez le format de livraison : rouleau continu, bande refendue, disques estampés ou flans découpés au laser.",
    pt: "Defina o formato de fornecimento: rolo mestre contínuo, bobina fendida, discos estampados ou peças cortadas a laser."
  },
  examples: {
    en: "Rolls: 1,220 mm width x 30.5 m length | Discs: Ø 125.0 mm (+0.0 / -0.5 mm)",
    zh: "卷料: 1,220 mm 宽 x 30.5 m 长 | 滤片: 直径 Ø 125.0 mm (+0.0 / -0.5 mm)",
    es: "Rollos: 1.220 mm ancho x 30,5 m largo | Discos: Ø 125,0 mm (+0,0 / -0,5 mm)",
    de: "Rollen: 1.220 mm Breite x 30,5 m Länge | Ronden: Ø 125,0 mm (+0,0 / -0,5 mm)",
    fr: "Rouleaux : 1 220 mm larg. x 30,5 m long. | Disques : Ø 125,0 mm (+0,0 / -0,5 mm)",
    pt: "Rolos: 1.220 mm larg. x 30,5 m comp. | Discos: Ø 125,0 mm (+0,0 / -0,5 mm)"
  },
  criticalNote: {
    en: "State whether selvage edges (natural woven edge) or sheared/trimmed edges are required for roll stock.",
    zh: "对于卷料，请注明需要天然织边（布边）还是经修边/切齐的边缘。",
    es: "Indique si para el material en rollo se requieren orillos naturales de telar o bordes cizallados/refilados.",
    de: "Geben Sie bei Rollenware an, ob feste Webkanten oder besäumte/geschnittene Kanten gefordert sind.",
    fr: "Précisez pour les rouleaux si des lisières tissées naturelles ou des bords cisaillés/ébavurés sont requis.",
    pt: "Indique se para rolos são necessárias ourelas naturais tecidas ou bordas aparadas/cisalhadas."
  },
  iconName: "ruler"
}, {
  id: "tolerances",
  step: "05",
  title: {
    en: "Manufacturing tolerances and standards",
    zh: "制造公差与执行标准",
    es: "Tolerancias de fabricación y normas",
    de: "Fertigungstoleranzen und Normen",
    fr: "Tolérances de fabrication et normes",
    pt: "Tolerâncias de fabricação e normas"
  },
  field: {
    en: "Standard & Grade",
    zh: "执行标准与等级 (Standard & Grade)",
    es: "Norma y grado (Standard & Grade)",
    de: "Norm & Klasse (Standard & Grade)",
    fr: "Norme & classe (Standard & Grade)",
    pt: "Norma e classe (Standard & Grade)"
  },
  description: {
    en: "Reference the governing international wire cloth fabrication standard to define acceptable aperture deviations and defect allowances.",
    zh: "注明适用的国际编织网制造标准，以界定孔径允许公差及表面缺陷容许范围。",
    es: "Indique la norma internacional de fabricación aplicable para fijar tolerancias de abertura y criterios de defectos admisibles.",
    de: "Geben Sie die maßgebliche Fertigungsnorm an, um zulässige Maschenweitenabweichungen und Fehlergrenzen zu definieren.",
    fr: "Référencez la norme de fabrication applicable pour encadrer les écarts d'ouverture et les critères de défauts admissibles.",
    pt: "Indique a norma internacional de fabricação aplicável para definir tolerâncias de abertura e tolerâncias de defeito."
  },
  examples: {
    en: "ASTM E2016, ISO 9044 Industrial Screen Class, or ISO 4783 Industrial Wire Screens",
    zh: "ASTM E2016, ISO 9044 工业筛网级, 或 ISO 4783 工业金属丝筛网",
    es: "ASTM E2016, ISO 9044 clase para cribas industriales, o ISO 4783",
    de: "ASTM E2016, ISO 9044 Industriesiebklasse, oder ISO 4783",
    fr: "ASTM E2016, ISO 9044 classe tamisage industriel, ou ISO 4783",
    pt: "ASTM E2016, ISO 9044 classe para peneiramento industrial, ou ISO 4783"
  },
  criticalNote: {
    en: "Standard industrial grade allows minor defect patches per roll; specify 'Critical Filter Grade' for defect-free filter elements.",
    zh: "标准工业级允许每卷存在微小织造修补；高洁净度滤芯请指定“关键过滤级 (Critical Filter Grade)”。",
    es: "El grado industrial estándar admite pequeñas correcciones por rollo; especifique 'Critical Filter Grade' para elementos sin defectos.",
    de: "Der Standard-Industriegrad erlaubt geringe Reparaturstellen pro Rolle; fordern Sie 'Critical Filter Grade' für fehlerfreie Filterelemente an.",
    fr: "Le grade industriel standard tolère des réparations mineures par rouleau ; spécifiez 'Critical Filter Grade' pour éléments sans défaut.",
    pt: "O grau industrial padrão aceita pequenos reparos por rolo; especifique 'Critical Filter Grade' para elementos filtrantes sem defeito."
  },
  iconName: "shield"
}, {
  id: "certification",
  step: "06",
  title: {
    en: "Testing and mill documentation",
    zh: "质检报告与原厂文档",
    es: "Ensayos y documentación de fábrica",
    de: "Prüfzeugnisse und Werksdokumentation",
    fr: "Essais et documentation d'usine",
    pt: "Ensaios e documentação de fábrica"
  },
  field: {
    en: "Quality Documentation",
    zh: "质量文件要求 (Quality Documentation)",
    es: "Documentación de calidad (Quality Documentation)",
    de: "Qualitätsdokumentation (Quality Documentation)",
    fr: "Documentation qualité (Quality Documentation)",
    pt: "Documentação de qualidade (Quality Documentation)"
  },
  description: {
    en: "List required metallurgical, dimensional, and compliance records required prior to shipment clearance.",
    zh: "列明出货放行前所需随附的冶金材料报告、尺寸检测及合规认证文件。",
    es: "Detalle los informes metalúrgicos, dimensionales y de conformidad requeridos antes del despacho del envío.",
    de: "Listen Sie alle vor dem Versandabschluss benötigten metallurgischen, maßlichen und Konformitätsnachweise auf.",
    fr: "Énumérez les rapports métallurgiques, dimensionnels et de conformité exigés avant le dédouanement et l'expédition.",
    pt: "Liste os relatórios metalúrgicos, dimensionais e de conformidade exigidos antes da liberação para expedição."
  },
  examples: {
    en: "EN 10204 3.1 material certificates (available when specified in the order), XRF PMI Test Report (available upon request), Bubble Point Data",
    zh: "EN 10204 3.1 材质证书（可按订单要求提供），XRF PMI 光谱合金成分报告（可按需提供），冒泡点测试数据",
    es: "Certificados de material EN 10204 3.1 (disponibles bajo pedido), informe XRF PMI (previa solicitud), datos de punto de burbuja",
    de: "Abnahmeprüfzeugnis EN 10204 3.1 (auf Anfrage bei Bestellung), XRF-PMI-Prüfbericht (auf Anfrage), Bubble-Point-Messwerte",
    fr: "Certificats de matière EN 10204 3.1 (disponibles sur demande à la commande), rapport XRF PMI (sur demande), données de point de bulle",
    pt: "Certificados de material EN 10204 3.1 (disponíveis sob solicitação no pedido), relatório de ensaio XRF PMI (sob pedido), dados de ponto de bolha"
  },
  criticalNote: {
    en: "Request 3.1 inspection certificates at time of order entry to ensure heat traceability back to master coil billets.",
    zh: "请在下单时同步索取 3.1 证书，以确保炉号可追溯至母卷线材。",
    es: "Solicite los certificados de inspección 3.1 en el momento del pedido para asegurar la trazabilidad de colada hasta el alambre base.",
    de: "Fordern Sie 3.1-Zeugnisse direkt bei Auftragserteilung an, um die Chargenrückverfolgbarkeit bis zum Vormaterial sicherzustellen.",
    fr: "Demandez les certificats d'inspection 3.1 dès la commande pour garantir la traçabilité de coulée jusqu'aux billettes d'origine.",
    pt: "Solicite os certificados de inspeção 3.1 no momento do pedido para assegurar a rastreabilidade da corrida até a bobina de arame."
  },
  iconName: "file"
}];
interface SamplePoRow {
  line: string;
  item: string;
  desc: Record<Lang, string>;
  qty: Record<Lang, string>;
  spec: Record<Lang, string>;
}
const samplePoLines: SamplePoRow[] = [{
  line: "01",
  item: "SS316L-PDW-24x110",
  desc: {
    en: "Stainless Steel 316L Plain Dutch Weave Filter Cloth, 24 x 110 Mesh/inch, Warp: 0.35mm / Weft: 0.25mm, 80 Micron Nominal Retention",
    zh: "不锈钢 316L 平纹密纹过滤网布, 24 x 110 目/英寸, 经丝: 0.35mm / 纬丝: 0.25mm, 名义截留精度 80 微米",
    es: "Tela filtrante de acero inoxidable 316L Dutch liso, 24 x 110 mallas/pulgada, urdimbre: 0,35 mm / trama: 0,25 mm, retención nominal 80 micras",
    de: "Edelstahl 316L Glatte Filtertresse, 24 x 110 Mesh/Zoll, Kette: 0,35 mm / Schuss: 0,25 mm, 80 µm Nennabscheidegrad",
    fr: "Toile filtrante inox 316L tissage hollandais uni, 24 x 110 mailles/pouce, chaîne : 0,35 mm / trame : 0,25 mm, rétention nominale 80 microns",
    pt: "Tela filtrante de aço inoxidável 316L holandesa lisa, 24 x 110 malhas/polegada, urdume: 0,35 mm / trama: 0,25 mm, retenção nominal 80 mícrons"
  },
  qty: {
    en: "2 Master Rolls (1,000 mm x 30 m)",
    zh: "2 卷原厂大卷 (1,000 mm x 30 m)",
    es: "2 rollos maestros (1.000 mm x 30 m)",
    de: "2 Mutterrollen (1.000 mm x 30 m)",
    fr: "2 rouleaux mères (1 000 mm x 30 m)",
    pt: "2 rolos mestres (1.000 mm x 30 m)"
  },
  spec: {
    en: "ASTM E2016 / ISO 9044, EN 10204 3.1 MTC available upon request",
    zh: "ASTM E2016 / ISO 9044, 可按订单要求提供 EN 10204 3.1 材质单",
    es: "ASTM E2016 / ISO 9044, certificado EN 10204 3.1 disponible bajo pedido",
    de: "ASTM E2016 / ISO 9044, EN 10204 3.1 Werkszeugnis auf Anfrage lieferbar",
    fr: "ASTM E2016 / ISO 9044, CCPU EN 10204 3.1 disponible sur demande",
    pt: "ASTM E2016 / ISO 9044, certificado EN 10204 3.1 disponível sob solicitação"
  }
}, {
  line: "02",
  item: "SS304-SQ-100-0.10",
  desc: {
    en: "Stainless Steel 304 Square Plain Weave Mesh, 100 x 100 Mesh/inch, Wire Dia: 0.10mm, Aperture: 150 Micron, 36.8% Open Area",
    zh: "不锈钢 304 方孔平纹编织网, 100 x 100 目/英寸, 丝径: 0.10mm, 孔径: 150 微米, 开孔率 36.8%",
    es: "Malla cuadrada de acero inoxidable 304 tejido liso, 100 x 100 mallas/pulgada, hilo: 0,10 mm, apertura: 150 micras, área abierta 36,8%",
    de: "Edelstahl 304 Quadratmaschen-Drahtgewebe, 100 x 100 Mesh/Zoll, Draht: 0,10 mm, Maschenweite: 150 µm, offene Fläche 36,8%",
    fr: "Toile métallique inox 304 tissage uni à mailles carrées, 100 x 100 mailles/pouce, fil : 0,10 mm, ouverture : 150 microns, surface ouverte 36,8%",
    pt: "Malha quadrada de aço inoxidável 304 trama lisa, 100 x 100 malhas/polegada, arame: 0,10 mm, abertura: 150 mícrons, área aberta 36,8%"
  },
  qty: {
    en: "500 Stamped Discs (Ø 75.0mm ±0.2mm)",
    zh: "500 片冲压圆片 (Ø 75.0mm ±0.2mm)",
    es: "500 discos estampados (Ø 75,0 mm ±0,2 mm)",
    de: "500 Stanzronden (Ø 75,0 mm ±0,2 mm)",
    fr: "500 disques découpés (Ø 75,0 mm ±0,2 mm)",
    pt: "500 discos estampados (Ø 75,0 mm ±0,2 mm)"
  },
  spec: {
    en: "Ultrasonically cleaned, degreased, ASTM E2016 Inspection",
    zh: "超声波清洗除油, ASTM E2016 检验合格",
    es: "Limpieza ultrasónica, desengrasado, inspección ASTM E2016",
    de: "Ultraschallgereinigt, entfettet, Prüfung nach ASTM E2016",
    fr: "Nettoyage par ultrasons, dégraissage, contrôle ASTM E2016",
    pt: "Limpeza ultrassônica, desengordurado, inspeção ASTM E2016"
  }
}];
const guideCopy = {
  badge: {
    en: "Procurement & Technical Guide",
    zh: "采购与技术规格指南",
    es: "Guía técnica y de compras",
    de: "Einkaufs- & technischer Leitfaden",
    fr: "Guide technique et d'approvisionnement",
    pt: "Guia técnico e de suprimentos"
  },
  h2: {
    en: "How to Specify Filter Mesh on Your Purchase Order",
    zh: "如何在采购订单 (PO) 中准确标注过滤网规格",
    es: "Cómo especificar la malla de filtración en su orden de compra",
    de: "Wie Sie Filtergewebe in Ihrer Bestellung präzise spezifizieren",
    fr: "Comment spécifier la toile de filtration sur votre bon de commande",
    pt: "Como especificar a malha de filtração no seu pedido de compra"
  },
  lead: {
    en: "Include these six core parameters to avoid manufacturing delays and ensure exact roll or piece delivery.",
    zh: "请包含以下六项核心技术参数，以避免制造延误并确保按图精准交付卷料或定制件。",
    es: "Incluya estos seis parámetros clave para evitar retrasos de fabricación y asegurar una entrega exacta de rollos o piezas.",
    de: "Berücksichtigen Sie diese sechs Kernparameter, um Verzögerungen zu vermeiden und eine maßgenaue Lieferung sicherzustellen.",
    fr: "Indiquez ces six paramètres clés pour éviter tout retard de fabrication et garantir une livraison conforme en rouleaux ou en pièces.",
    pt: "Inclua estes seis parâmetros fundamentais para evitar atrasos na fabricação e garantir a entrega exata de rolos ou peças."
  },
  recommendedNotation: {
    en: "Recommended notation:",
    zh: "推荐标注格式：",
    es: "Formato recomendado:",
    de: "Empfohlene Schreibweise:",
    fr: "Format recommandé :",
    pt: "Formato recomendado:"
  },
  tip: {
    en: "Tip: ",
    zh: "提示：",
    es: "Consejo: ",
    de: "Hinweis: ",
    fr: "Conseil : ",
    pt: "Dica: "
  },
  sampleTitle: {
    en: "Sample purchase order line format",
    zh: "采购订单行项目标准标注示例",
    es: "Ejemplo de línea en orden de compra",
    de: "Beispiel für eine Bestellposition",
    fr: "Exemple de ligne sur bon de commande",
    pt: "Exemplo de linha de pedido de compra"
  },
  sampleSub: {
    en: "Standard format accepted by our engineering and production departments.",
    zh: "工程部与生产车间通用的标准化 PO 格式规范。",
    es: "Formato estándar utilizado por nuestros departamentos técnico y de producción.",
    de: "Standardformat, wie es von Technik und Fertigung verarbeitet wird.",
    fr: "Format standard exploité par nos équipes techniques et de production.",
    pt: "Formato padrão aceito pelos nossos setores de engenharia e produção."
  },
  thLine: {
    en: "Ln",
    zh: "序号",
    es: "N.º",
    de: "Pos.",
    fr: "Ligne",
    pt: "Linha"
  },
  thPartCode: {
    en: "Part Code",
    zh: "物料编号",
    es: "Código de artículo",
    de: "Artikelcode",
    fr: "Réf. pièce",
    pt: "Código do item"
  },
  thDesc: {
    en: "Specification & Description",
    zh: "规格与产品描述",
    es: "Especificación y descripción",
    de: "Spezifikation & Beschreibung",
    fr: "Spécification & description",
    pt: "Especificação & descrição"
  },
  thQty: {
    en: "Quantity",
    zh: "数量",
    es: "Cantidad",
    de: "Menge",
    fr: "Quantité",
    pt: "Quantidade"
  },
  traceabilityNotice: {
    en: "All line items verified through our quality traceability system",
    zh: "所有订单行项目均经过质量追溯体系核验",
    es: "Todas las líneas son verificadas mediante nuestro sistema de trazabilidad",
    de: "Alle Positionen werden über unser Rückverfolgbarkeitssystem geprüft",
    fr: "Toutes les lignes sont tracées via notre système de contrôle qualité",
    pt: "Todas as posições são conferidas pelo nosso sistema de rastreabilidade"
  },
  customPartTooltipTitle: {
    en: "Need custom part numbering?",
    zh: "需要自定义零件编号映射？",
    es: "¿Necesita numeración personalizada?",
    de: "Eigene Teilenummern gewünscht?",
    fr: "Besoin d'un repérage spécifique ?",
    pt: "Precisa de código de peça personalizado?"
  },
  customPartTooltipContent: {
    en: "We map your internal ERP part codes directly to production batch records.",
    zh: "我们可将您公司的内部 ERP 物料代码直接关联至生产批次追溯记录中。",
    es: "Asociamos sus códigos internos de ERP directamente a los registros de lote de fabricación.",
    de: "Wir ordnen Ihre internen ERP-Artikelnummern direkt den Produktionschargen zu.",
    fr: "Nous intégrons vos codes articles ERP internes directement aux fiches de lots.",
    pt: "Vinculamos seus códigos internos de ERP aos registros de lote de produção."
  },
  copyBlockTitle: {
    en: "Copyable specification block",
    zh: "可快速复制的规格文本块",
    es: "Bloque de especificaciones copiable",
    de: "Kopierbarer Spezifikationstext",
    fr: "Bloc de spécifications à copier",
    pt: "Bloco de especificações copiável"
  },
  copyBlockSub: {
    en: "Paste directly into your requisition notes, RFQ email, or ERP purchasing notes.",
    zh: "可直接粘贴至您的申购说明、询价邮件或 ERP 采购备注中。",
    es: "Péguelo directamente en su solicitud, correo de cotización o notas de compra del ERP.",
    de: "Fügen Sie diesen Text direkt in Ihre Bestellanforderung, Anfrage-Mail oder ERP-Bestellnotiz ein.",
    fr: "Collez directement ce texte dans votre demande d'achat, e-mail de devis ou bon ERP.",
    pt: "Cole diretamente na sua requisição de compra, e-mail de cotação ou observações do ERP."
  },
  copied: {
    en: "Copied",
    zh: "已复制",
    es: "Copiado",
    de: "Kopiert",
    fr: "Copié",
    pt: "Copiado"
  },
  copyFormat: {
    en: "Copy format",
    zh: "复制格式",
    es: "Copiar formato",
    de: "Format kopieren",
    fr: "Copier le format",
    pt: "Copiar formato"
  },
  downloadChecklistNote: {
    en: "Download printable PDF checklist with dimensional tolerance tables.",
    zh: "下载含尺寸公差对照表的 PDF 采购核对清单。",
    es: "Descargue la lista de verificación en PDF con tablas de tolerancias.",
    de: "Druckfertige PDF-Checkliste mit Maßtoleranztabellen herunterladen.",
    fr: "Téléchargez la fiche mémo PDF imprimable avec tableaux de tolérances.",
    pt: "Baixe o checklist em PDF para impressão com tabelas de tolerâncias."
  },
  downloadChecklistBtn: {
    en: "Download guide PDF",
    zh: "下载指南 PDF",
    es: "Descargar guía PDF",
    de: "Leitfaden PDF herunterladen",
    fr: "Télécharger le guide PDF",
    pt: "Baixar guia PDF"
  },
  bottomPrompt: {
    en: "Unsure of your exact mesh opening, wire diameter, or alloy requirements?",
    zh: "不确定具体的筛孔开孔、丝径或合金选型？",
    es: "¿Dudas con la abertura exacta, el diámetro del alambre o la aleación?",
    de: "Unsicher bei Maschenweite, Drahtstärke oder Legierungsauswahl?",
    fr: "Un doute sur l'ouverture exacte, le diamètre du fil ou le choix d'alliage ?",
    pt: "Em dúvida sobre a abertura exata, diâmetro do arame ou liga metálica?"
  }
};
const sampleSnippets: Record<Lang, string> = {
  en: `MUTUALFILTER PURCHASE ORDER SPECIFICATION FORMAT:
--------------------------------------------------
Item 1: Stainless Steel Woven Filter Mesh
- Alloy Grade: AISI 316L (UNS S31603)
- Weave Type: Plain Dutch Weave (PDW)
- Mesh Count: 24 x 110 mesh per linear inch
- Wire Diameter: Warp 0.35 mm / Weft 0.25 mm
- Nominal Aperture: 80 Micron (0.080 mm)
- Supply Format: 1,220 mm Width x 30.5 m Length Roll
- Edge Condition: Clean sheared edges, deburred
- Inspection Standard: ASTM E2016 Class A
- Required Certificates: EN 10204 Type 3.1 Mill Test Certificate & PMI Report (upon request)`,
  zh: `MUTUALFILTER 采购订单规格标准格式：
--------------------------------------------------
项目 1: 不锈钢编织过滤丝网
- 材质牌号: AISI 316L (UNS S31603)
- 编织类型: 平纹密纹编织 (PDW)
- 网孔目数: 24 x 110 目/英寸
- 丝径尺寸: 经丝 0.35 mm / 纬丝 0.25 mm
- 名义孔径: 80 微米 (0.080 mm)
- 供货形式: 1,220 mm 宽 x 30.5 m 长 卷料
- 边缘要求: 剪边平整无毛刺
- 检验标准: ASTM E2016 Class A 级
- 随附文档: 可按订单要求提供 EN 10204 3.1 材质证书及 PMI 报告`,
  es: `FORMATO DE ESPECIFICACIÓN DE ORDEN DE COMPRA MUTUALFILTER:
--------------------------------------------------
Ítem 1: Malla filtrante tejida de acero inoxidable
- Calidad de aleación: AISI 316L (UNS S31603)
- Tipo de tejido: Dutch liso (PDW)
- Número de mallas: 24 x 110 mallas por pulgada lineal
- Diámetro del alambre: Urdimbre 0,35 mm / Trama 0,25 mm
- Apertura nominal: 80 micras (0,080 mm)
- Formato de suministro: Rollo de 1.220 mm ancho x 30,5 m largo
- Acabado de bordes: Bordes cizallados limpios y desbarbados
- Norma de inspección: ASTM E2016 Clase A
- Certificados requeridos: EN 10204 3.1 e informe PMI (bajo pedido)`,
  de: `MUTUALFILTER BESTELL-SPEZIFIKATIONSFORMAT:
--------------------------------------------------
Position 1: Edelstahl-Filterdrahtgewebe
- Werkstoffgüte: AISI 316L (UNS S31603)
- Webart: Glatte Tresse (PDW)
- Maschenzahl: 24 x 110 Mesh pro linearem Zoll
- Drahtdurchmesser: Kette 0,35 mm / Schuss 0,25 mm
- Nennmaschenweite: 80 Mikron (0,080 mm)
- Lieferform: Rolle 1.220 mm Breite x 30,5 m Länge
- Kantenausführung: Sauber besäumt und entgratet
- Prüfnorm: ASTM E2016 Klasse A
- Prüfbescheinigungen: EN 10204 3.1 Werkszeugnis & PMI-Bericht (auf Anfrage)`,
  fr: `FORMAT DE SPÉCIFICATION POUR COMMANDE MUTUALFILTER :
--------------------------------------------------
Poste 1 : Toile filtrante tissée en acier inoxydable
- Nuance d'alliage : AISI 316L (UNS S31603)
- Armure : Tissage hollandais uni (PDW)
- Nombre de mailles : 24 x 110 mailles par pouce linéaire
- Diamètre du fil : Chaîne 0,35 mm / Trame 0,25 mm
- Ouverture nominale : 80 microns (0,080 mm)
- Format de livraison : Rouleau larg. 1 220 mm x long. 30,5 m
- Finition des rives : Bords cisaillés propres, ébavurés
- Norme de contrôle : ASTM E2016 Classe A
- Certificats requis : CCPU EN 10204 3.1 et rapport PMI (sur demande)`,
  pt: `FORMATO DE ESPECIFICAÇÃO DE PEDIDO DE COMPRA MUTUALFILTER:
--------------------------------------------------
Item 1: Tela filtrante tecida de aço inoxidável
- Grau da liga: AISI 316L (UNS S31603)
- Tipo de trama: Holandesa lisa (PDW)
- Contagem de malha: 24 x 110 malhas por polegada linear
- Diâmetro do arame: Urdume 0,35 mm / Trama 0,25 mm
- Abertura nominal: 80 mícrons (0,080 mm)
- Formato de fornecimento: Rolo com 1.220 mm de largura x 30,5 m de comprimento
- Acabamento das bordas: Bordas cisalhadas limpas e rebarbadas
- Norma de inspeção: ASTM E2016 Classe A
- Certificados requeridos: Certificado EN 10204 3.1 e relatório PMI (sob pedido)`
};
export default function OrderGuide() {
  const {
    lang
  } = useLanguage();
  const [copied, setCopied] = useState(false);
  const snippet = sampleSnippets[lang] || sampleSnippets.en;
  const handleCopy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  const getIcon = (iconName: SpecItem["iconName"]) => {
    switch (iconName) {
      case "layers":
        return <Layers className="size-5 text-primary" aria-hidden="true" />;
      case "sliders":
        return <SlidersHorizontal className="size-5 text-primary" aria-hidden="true" />;
      case "ruler":
        return <Ruler className="size-5 text-primary" aria-hidden="true" />;
      case "shield":
        return <ShieldCheck className="size-5 text-primary" aria-hidden="true" />;
      case "file":
        return <FileCheck className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  const columns: DataTableColumn<SamplePoRow>[] = useMemo(() => [{
    key: "line",
    header: pick(lang, guideCopy.thLine),
    cell: row => <span className="font-mono text-sm font-semibold text-muted-foreground">
            {row.line}
          </span>,
    sticky: true
  }, {
    key: "item",
    header: pick(lang, guideCopy.thPartCode),
    cell: row => <span className="font-mono text-sm font-semibold text-foreground">
            {row.item}
          </span>
  }, {
    key: "desc",
    header: pick(lang, guideCopy.thDesc),
    cell: row => <div className="space-y-1 py-1">
            <div className="text-sm text-foreground font-medium">
              {pick(lang, row.desc)}
            </div>
            <div className="mf-caption font-mono text-primary">
              {pick(lang, row.spec)}
            </div>
          </div>
  }, {
    key: "qty",
    header: pick(lang, guideCopy.thQty),
    align: "right",
    cell: row => <span className="text-sm font-mono text-foreground font-semibold">
            {pick(lang, row.qty)}
          </span>
  }], [lang]);
  return <section id="purchaseorderspecificationguide" data-nav="light" className="w-full bg-muted/50 text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted text-foreground font-mono mf-caption mb-3 border border-border">
              <FileText className="size-3.5 text-primary" aria-hidden="true" />
              <span>{pick(lang, guideCopy.badge)}</span>
            </div>
            <h2 className="mf-h2 font-default text-foreground tracking-tight">
              {pick(lang, guideCopy.h2)}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {pick(lang, guideCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* 6 Essential PO Elements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {specItems.map((item, i) => <Reveal key={item.id} delay={Math.min(i * 60, 300)}>
              <Card className="h-full border border-border bg-card text-card-foreground transition-colors duration-200 hover:border-primary/40">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                      {getIcon(item.iconName)}
                    </div>
                    <span className="font-mono text-sm font-semibold px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                      {item.step}
                    </span>
                  </div>
                  <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                    {pick(lang, item.title)}
                  </CardTitle>
                  <CardDescription className="mf-caption uppercase font-mono tracking-wider text-primary font-medium mt-1">
                    {pick(lang, item.field)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <p className="text-muted-foreground leading-normal">
                    {pick(lang, item.description)}
                  </p>

                  <div className="rounded-md bg-muted/60 p-3 border border-border/70 space-y-1.5">
                    <span className="text-sm font-semibold uppercase tracking-wide text-foreground/80 block">
                      {pick(lang, guideCopy.recommendedNotation)}
                    </span>
                    <p className="text-sm font-mono text-foreground font-medium break-words">
                      {pick(lang, item.examples)}
                    </p>
                  </div>

                  <div className="rounded-md bg-muted/40 p-3 border border-border flex items-start gap-2">
                    <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-sm text-foreground leading-snug">
                      <strong className="font-semibold text-foreground">
                        {pick(lang, guideCopy.tip)}
                      </strong>
                      {pick(lang, item.criticalNote)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Reveal>)}
        </div>

        {/* PO Line Item Matrix & Code Block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 items-start">
          {/* Sample Line Items Table */}
          <Reveal className="lg:col-span-7">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <div>
                  <h3 className="mf-h3 font-default text-foreground">
                    {pick(lang, guideCopy.sampleTitle)}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {pick(lang, guideCopy.sampleSub)}
                  </p>
                </div>
                <Badge variant="outline" className="w-fit border-border text-foreground font-mono text-sm">
                  PO Template v2.4
                </Badge>
              </div>

              <DataTable columns={columns} rows={samplePoLines} getRowKey={row => row.line} minWidth={600} />

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground pt-2">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="size-4 text-primary" aria-hidden="true" />
                  <span>{pick(lang, guideCopy.traceabilityNotice)}</span>
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 text-primary cursor-pointer hover:underline">
                      <CircleHelp className="size-3.5" aria-hidden="true" />
                      <span>{pick(lang, guideCopy.customPartTooltipTitle)}</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{pick(lang, guideCopy.customPartTooltipContent)}</span>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </Reveal>

          {/* Copyable Quick-Format Card */}
          <Reveal delay={120} className="lg:col-span-5">
            <div className="bg-card rounded-lg border border-border p-6 flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileCheck className="size-4 text-primary" aria-hidden="true" />
                    <span className="text-sm font-semibold text-foreground">
                      {pick(lang, guideCopy.copyBlockTitle)}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={handleCopy} className="gap-1.5 text-sm font-medium">
                    {copied ? <>
                        <Check className="size-3.5 text-primary" aria-hidden="true" />
                        <span>{pick(lang, guideCopy.copied)}</span>
                      </> : <>
                        <Copy className="size-3.5" aria-hidden="true" />
                        <span>{pick(lang, guideCopy.copyFormat)}</span>
                      </>}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {pick(lang, guideCopy.copyBlockSub)}
                </p>

                <pre className="p-4 rounded-lg bg-muted text-foreground font-mono mf-caption leading-relaxed overflow-x-auto border border-border select-all whitespace-pre-wrap">
                  {snippet}
                </pre>
              </div>

              <div className="mt-6 pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground text-center sm:text-left">
                  {pick(lang, guideCopy.downloadChecklistNote)}
                </span>
                <Button asChild size="sm" variant="outline" className="w-full sm:w-auto shrink-0">
                  <Link to="/DatasheetsPage">
                    <Download className="size-3.5" aria-hidden="true" />
                    <span>{pick(lang, guideCopy.downloadChecklistBtn)}</span>
                  </Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Single mid-page prompt: plain link to quote / inquiry */}
        <Reveal delay={150}>
          <div className="rounded-lg border border-border bg-card p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">
              {pick(lang, guideCopy.bottomPrompt)}
            </span>
            <Link to="/RequestAQuotePage" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0">
              <span>{pick(lang, COMMON.requestQuote)}</span>
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>;
}